import { resolveCleicAppChannel } from './app-channel.js';

const CHANNEL_LABEL = Object.freeze({
  live: 'Live',
  preview: 'Preview',
  lab: 'Lab',
});

const APP_VERSION_FALLBACK = '4.0.0';
const LAB_SEED_DB_MARKER = 'lab-seed-20260517a';
const LAB_MVP_SOURCE_MARKER = 'lab-mvp-source-20260517b';
export const CLEIC_HUMAN_CURRENTNESS_STAMP_SOURCE_MARKER =
  'cleic-human-currentness-stamp-20260518a';

function buildConst(value) {
  return String(value ?? '').trim();
}

function getBuildConst(name) {
  try {
    if (name === 'version' && typeof __CLEIC_APP_VERSION__ !== 'undefined') return __CLEIC_APP_VERSION__;
    if (name === 'sha' && typeof __CLEIC_GIT_SHA__ !== 'undefined') return __CLEIC_GIT_SHA__;
    if (name === 'ref' && typeof __CLEIC_GIT_REF__ !== 'undefined') return __CLEIC_GIT_REF__;
    if (name === 'env' && typeof __CLEIC_VERCEL_ENV__ !== 'undefined') return __CLEIC_VERCEL_ENV__;
    if (name === 'deployment' && typeof __CLEIC_DEPLOYMENT_ID__ !== 'undefined') return __CLEIC_DEPLOYMENT_ID__;
    if (name === 'built' && typeof __CLEIC_BUILD_TIME__ !== 'undefined') return __CLEIC_BUILD_TIME__;
  } catch {
    return '';
  }
  const globalKey = {
    version: '__CLEIC_APP_VERSION__',
    sha: '__CLEIC_GIT_SHA__',
    ref: '__CLEIC_GIT_REF__',
    env: '__CLEIC_VERCEL_ENV__',
    deployment: '__CLEIC_DEPLOYMENT_ID__',
    built: '__CLEIC_BUILD_TIME__',
  }[name];
  const globalValue = globalThis[globalKey];
  return typeof globalValue === 'string' ? buildConst(globalValue) : '';
}

function shortDeploymentMarker(value) {
  const clean = String(value || '')
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')
    .replace(/\.vercel\.app$/i, '')
    .trim();
  if (!clean) return '';
  if (/^dpl_/i.test(clean)) return clean.slice(0, 10);

  const appMatch = clean.match(/^cleic-(?:ledger|lens)-([a-z0-9]+)-/i);
  if (appMatch?.[1]) return `url:${appMatch[1].slice(0, 8)}`;

  return clean.length > 14 ? clean.slice(0, 14) : clean;
}

function shortBuildTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 14);
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hour = String(date.getUTCHours()).padStart(2, '0');
  const minute = String(date.getUTCMinutes()).padStart(2, '0');
  return `b:${month}${day}T${hour}${minute}Z`;
}

function formatBuildDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pht = new Date(date.getTime() + 8 * 60 * 60 * 1000);
  const year = pht.getUTCFullYear();
  const month = String(pht.getUTCMonth() + 1).padStart(2, '0');
  const day = String(pht.getUTCDate()).padStart(2, '0');
  const hour = String(pht.getUTCHours()).padStart(2, '0');
  const minute = String(pht.getUTCMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hour}:${minute} PHT`;
}

function shortSha(sha) {
  return sha ? sha.slice(0, 7) : '';
}

function humanizeMarkerDate(marker) {
  const match = String(marker || '').match(/(\d{4})(\d{2})(\d{2})([a-z]?)/i);
  if (!match) return marker;
  const [, year, month, day, suffix] = match;
  return `${year}-${month}-${day}${suffix.toLowerCase()}`;
}

export function getCleicReleaseInfo(channel = resolveCleicAppChannel() ?? 'live') {
  const safeChannel = CHANNEL_LABEL[channel] ? channel : 'live';
  const appVersion = buildConst(getBuildConst('version')) || APP_VERSION_FALLBACK;
  const gitSha = buildConst(getBuildConst('sha'));
  const gitRef = buildConst(getBuildConst('ref'));
  const vercelEnv = buildConst(getBuildConst('env'));
  const deploymentId = buildConst(getBuildConst('deployment'));
  const buildTime = buildConst(getBuildConst('built'));
  const buildDateTimeLabel = formatBuildDateTime(buildTime);
  const gitShortSha = shortSha(gitSha);
  const channelLabel = CHANNEL_LABEL[safeChannel];
  const humanReleaseMarker = humanizeMarkerDate(
    CLEIC_HUMAN_CURRENTNESS_STAMP_SOURCE_MARKER,
  );
  const artifactMarker = shortDeploymentMarker(deploymentId) || shortBuildTime(buildTime);
  const releaseMarker = [artifactMarker, gitShortSha].filter(Boolean).join(' · ')
    || CLEIC_HUMAN_CURRENTNESS_STAMP_SOURCE_MARKER;
  const visibleReleaseMarker = releaseMarker.startsWith('cleic-human-currentness-stamp-')
    ? humanReleaseMarker
    : releaseMarker;
  const label = [
    channelLabel,
    appVersion ? `v${appVersion.replace(/^v/i, '')}` : null,
    artifactMarker || gitShortSha || null,
  ].filter(Boolean).join(' · ');
  const detail = [
    `Channel: ${channelLabel}`,
    safeChannel === 'lab' ? `Lab seed DB marker: ${LAB_SEED_DB_MARKER}` : null,
    safeChannel === 'lab' ? `Lab MVP source marker: ${LAB_MVP_SOURCE_MARKER}` : null,
    `Human currentness stamp: ${CLEIC_HUMAN_CURRENTNESS_STAMP_SOURCE_MARKER}`,
    `Visible stamp: ${[
      channelLabel,
      appVersion ? `v${appVersion.replace(/^v/i, '')}` : null,
      buildDateTimeLabel || null,
      visibleReleaseMarker,
    ].filter(Boolean).join(' · ')}`,
    `Release marker: ${releaseMarker}`,
    appVersion ? `App version: ${appVersion}` : null,
    gitRef ? `Git ref: ${gitRef}` : null,
    gitSha ? `Git SHA: ${gitSha}` : null,
    vercelEnv ? `Vercel env: ${vercelEnv}` : null,
    deploymentId ? `Deployment: ${deploymentId}` : null,
    buildTime ? `Built: ${buildTime}` : null,
  ].filter(Boolean).join('\n');
  const currentnessStamp = [
    channelLabel,
    appVersion ? `v${appVersion.replace(/^v/i, '')}` : null,
    releaseMarker,
  ].filter(Boolean).join(' · ');
  const humanCurrentnessStamp = [
    channelLabel,
    appVersion ? `v${appVersion.replace(/^v/i, '')}` : null,
    buildDateTimeLabel || null,
    visibleReleaseMarker,
  ].filter(Boolean).join(' · ');

  return {
    channel: safeChannel,
    channelLabel,
    appVersion,
    gitSha,
    gitShortSha,
    gitRef,
    vercelEnv,
    deploymentId,
    buildTime,
    buildDateTimeLabel,
    label,
    releaseMarker,
    humanReleaseMarker,
    currentnessStamp,
    humanCurrentnessStamp,
    detail,
  };
}
